import React, { useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Row,
  Col,
  Button,
  message,
} from "antd";
import { DollarOutlined, FileTextOutlined, CreditCardOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  useCreateAccountingEntryMutation,
  useUpdateAccountingEntryMutation,
} from "../../store/api/accountingApi";
import type { AccountingEntryResponse } from "@OrianaTypes";
import type { POData } from "../../store/poSlice";
import type { DispatchResponse } from "@OrianaTypes";

const PAYMENT_TYPE_ADVANCE = "advance";
const PAYMENT_TYPE_PART_PAYMENT = "part_payment";
const PAYMENT_TYPE_CREDIT_NOTE = "credit_note";

const paymentTypeOptions = [
  { value: PAYMENT_TYPE_ADVANCE, label: "Advance" },
  { value: PAYMENT_TYPE_PART_PAYMENT, label: "Part Payment" },
  { value: PAYMENT_TYPE_CREDIT_NOTE, label: "Credit Note" },
];

const noDuesClearanceOptions = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "pending", label: "Pending" },
];

const paymentModeOptions = [
  { value: "neft", label: "NEFT" },
  { value: "rtgs", label: "RTGS" },
  { value: "cheque", label: "Cheque" },
  { value: "upi", label: "UPI" },
  { value: "cash", label: "Cash" },
  { value: "dd", label: "Demand Draft" },
];

const sectionStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "10px 14px",
  borderRadius: "10px",
  marginBottom: "16px",
  marginTop: "8px",
  fontWeight: 600,
  fontSize: "13px",
  background: "linear-gradient(135deg, #667eea15 0%, #764ba215 100%)",
  border: "1px solid #667eea30",
  color: "#4c51bf",
};

const currencyStyle: React.CSSProperties = { width: "100%" };

interface AddPaymentModalProps {
  open: boolean;
  onClose: () => void;
  poId: string;
  poData?: POData | null;
  dispatches?: DispatchResponse[];
  editRecord?: AccountingEntryResponse | null;
  totalPoCost: number;
  totalAdvanceReceived: number;
  previousBalance: number;
}

const AddPaymentModal: React.FC<AddPaymentModalProps> = ({
  open,
  onClose,
  poId,
  poData,
  dispatches = [],
  editRecord = null,
  totalPoCost,
  totalAdvanceReceived,
  previousBalance,
}) => {
  const [form] = Form.useForm();
  const isEditMode = !!editRecord;
  const paymentType = Form.useWatch("paymentType", form);
  const paymentAmount = Form.useWatch("paymentAmount", form);
  const advanceAmount = Form.useWatch("advanceAmount", form);
  const creditNoteAmount = Form.useWatch("creditNoteAmount", form);
  const tdsValue = Form.useWatch("tds", form);

  const [createEntry, { isLoading: isCreating }] = useCreateAccountingEntryMutation();
  const [updateEntry, { isLoading: isUpdating }] = useUpdateAccountingEntryMutation();
  const isLoading = isCreating || isUpdating;

  const isFirstPartPayment = previousBalance === 0 || previousBalance === totalPoCost - totalAdvanceReceived;

  // Auto-compute balance for part payment
  useEffect(() => {
    if (paymentType !== PAYMENT_TYPE_PART_PAYMENT || isEditMode) return;
    const payment = typeof paymentAmount === "number" ? paymentAmount : 0;
    const tds = typeof tdsValue === "number" ? tdsValue : 0;
    const totalOutflow = payment + tds;
    let balance: number;
    if (isFirstPartPayment) {
      balance = Math.max(0, totalPoCost - totalAdvanceReceived - totalOutflow);
    } else {
      balance = Math.max(0, previousBalance - totalOutflow);
    }
    form.setFieldValue("balanceAmount", parseFloat(balance.toFixed(2)));
  }, [paymentType, paymentAmount, tdsValue, totalPoCost, totalAdvanceReceived, previousBalance, isFirstPartPayment, isEditMode, form]);

  useEffect(() => {
    if (!open) return;

    if (isEditMode && editRecord) {
      const inferredType =
        editRecord.paymentType === PAYMENT_TYPE_ADVANCE
          ? PAYMENT_TYPE_ADVANCE
          : editRecord.paymentType === PAYMENT_TYPE_PART_PAYMENT
          ? PAYMENT_TYPE_PART_PAYMENT
          : editRecord.paymentType === PAYMENT_TYPE_CREDIT_NOTE
          ? PAYMENT_TYPE_CREDIT_NOTE
          : editRecord.creditNoteRef
          ? PAYMENT_TYPE_CREDIT_NOTE
          : PAYMENT_TYPE_PART_PAYMENT;

      form.setFieldsValue({
        paymentType: inferredType,
        taxInvoiceNo: editRecord.taxInvoiceNo,
        taxInvoiceDate: editRecord.taxInvoiceDate ? dayjs(editRecord.taxInvoiceDate) : undefined,
        invoiceAmount: editRecord.invoiceAmount ? parseFloat(editRecord.invoiceAmount) : undefined,
        dispatchId: editRecord.dispatchId,

        paymentMode: editRecord.paymentMode,
        advanceAmount:
          editRecord.paymentType === PAYMENT_TYPE_ADVANCE && editRecord.paymentAmount
            ? parseFloat(editRecord.paymentAmount)
            : undefined,
        advanceDate:
          editRecord.paymentType === PAYMENT_TYPE_ADVANCE && editRecord.paymentDate
            ? dayjs(editRecord.paymentDate)
            : undefined,
        multiplePaymentRefNo: editRecord.multiplePaymentRefNo,

        paymentDate: editRecord.paymentDate ? dayjs(editRecord.paymentDate) : undefined,
        paymentDueDate: editRecord.paymentDueDate ? dayjs(editRecord.paymentDueDate) : undefined,
        paymentAmount: editRecord.paymentAmount ? parseFloat(editRecord.paymentAmount) : undefined,
        tds: editRecord.tds ? parseFloat(editRecord.tds) : undefined,
        balanceAmount: editRecord.balanceAmount ? parseFloat(editRecord.balanceAmount) : undefined,
        noDuesClearanceStatus: editRecord.noDuesClearanceStatus,

        creditNoteRef: editRecord.creditNoteRef,
        creditNoteDate:
          editRecord.paymentType === PAYMENT_TYPE_CREDIT_NOTE && editRecord.paymentDate
            ? dayjs(editRecord.paymentDate)
            : undefined,
        creditNoteAmount:
          editRecord.paymentType === PAYMENT_TYPE_CREDIT_NOTE && editRecord.paymentAmount
            ? parseFloat(editRecord.paymentAmount)
            : undefined,
        creditNoteRemark: editRecord.creditNoteRemark,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        paymentType: PAYMENT_TYPE_ADVANCE,
        taxInvoiceNo: poData?.osgPiNo,
        taxInvoiceDate: poData?.osgPiDate ? dayjs(poData.osgPiDate) : undefined,
        invoiceAmount: totalPoCost > 0 ? totalPoCost : undefined,
      });
    }
  }, [open, isEditMode, editRecord, form, poData, totalPoCost]);

  const formatDate = (d: dayjs.Dayjs | undefined) => (d ? d.format("YYYY-MM-DD") : undefined);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const basePayload = {
        taxInvoiceNo: values.taxInvoiceNo,
        taxInvoiceDate: formatDate(values.taxInvoiceDate),
        invoiceAmount: values.invoiceAmount,
        dispatchId: values.dispatchId,
      };

      if (values.paymentType === PAYMENT_TYPE_ADVANCE) {
        // Balance after this advance = Invoice total − (other advances + this advance)
        const advanceAmt = Number(values.advanceAmount) || 0;
        const otherAdvances = isEditMode && editRecord?.paymentType === "advance" && editRecord.paymentAmount
          ? totalAdvanceReceived - parseFloat(editRecord.paymentAmount)
          : totalAdvanceReceived;
        const balanceAfterAdvance = Math.max(
          0,
          totalPoCost - otherAdvances - advanceAmt,
        );
        const payload = {
          ...basePayload,
          paymentType: PAYMENT_TYPE_ADVANCE,
          paymentMode: values.paymentMode,
          paymentDate: formatDate(values.advanceDate),
          paymentAmount: values.advanceAmount,
          multiplePaymentRefNo: values.multiplePaymentRefNo,
          balanceAmount: parseFloat(balanceAfterAdvance.toFixed(2)),
        };
        if (isEditMode && editRecord) {
          await updateEntry({
            id: editRecord.id,
            data: { ...payload },
            poId,
          }).unwrap();
        } else {
          await createEntry({
            poId,
            moduleType: "order",
            ...payload,
          }).unwrap();
        }
        message.success("Advance payment saved successfully");
      } else if (values.paymentType === PAYMENT_TYPE_PART_PAYMENT) {
        const payload = {
          ...basePayload,
          paymentType: PAYMENT_TYPE_PART_PAYMENT,
          paymentDate: formatDate(values.paymentDate),
          paymentDueDate: formatDate(values.paymentDueDate),
          paymentAmount: values.paymentAmount,
          tds: values.tds,
          balanceAmount: values.balanceAmount,
          noDuesClearanceStatus: values.noDuesClearanceStatus,
          paymentMode: values.paymentMode,
          multiplePaymentRefNo: values.multiplePaymentRefNo,
        };
        if (isEditMode && editRecord) {
          await updateEntry({ id: editRecord.id, data: payload, poId }).unwrap();
        } else {
          await createEntry({
            poId,
            moduleType: "order",
            ...payload,
          }).unwrap();
        }
        message.success("Part payment saved successfully");
      } else {
        // Credit note is payment towards invoice: balance after = balance before this entry − credit note amount
        const creditAmt = Number(values.creditNoteAmount) || 0;
        const existingCredit = isEditMode && editRecord?.paymentType === "credit_note" && editRecord.paymentAmount
          ? parseFloat(editRecord.paymentAmount)
          : 0;
        const balanceBefore = isEditMode && existingCredit
          ? previousBalance + existingCredit
          : previousBalance;
        const balanceAfterCreditNote = Math.max(0, balanceBefore - creditAmt);
        const payload = {
          ...basePayload,
          paymentType: PAYMENT_TYPE_CREDIT_NOTE,
          paymentDate: formatDate(values.creditNoteDate),
          paymentAmount: values.creditNoteAmount,
          creditNoteRef: values.creditNoteRef,
          creditNoteRemark: values.creditNoteRemark,
          balanceAmount: parseFloat(balanceAfterCreditNote.toFixed(2)),
        };
        if (isEditMode && editRecord) {
          await updateEntry({ id: editRecord.id, data: payload, poId }).unwrap();
        } else {
          await createEntry({
            poId,
            moduleType: "order",
            ...payload,
          }).unwrap();
        }
        message.success("Credit note saved successfully");
      }

      form.resetFields();
      onClose();
    } catch (err) {
      if (err && typeof err === "object" && "errorFields" in err) return;
      message.error("Failed to save. Please try again.");
    }
  };

  const dispatchOptions = dispatches.map((d) => ({
    value: d.dispatchId,
    label: `Dispatch #${d.dispatchId}${d.taxInvoiceNumber ? ` — ${d.taxInvoiceNumber}` : ""}`,
  }));

  return (
    <Modal
      title={
        <span style={{ fontWeight: 700, fontSize: "16px" }}>
          {isEditMode ? "Edit Payment Entry" : "Add Payment"}
        </span>
      }
      open={open}
      onCancel={() => {
        form.resetFields();
        onClose();
      }}
      width={720}
      destroyOnClose
      styles={{ body: { maxHeight: "75vh", overflowY: "auto", padding: "16px 24px" } }}
      footer={[
        <Button key="cancel" onClick={() => { form.resetFields(); onClose(); }} disabled={isLoading}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          loading={isLoading}
          style={{ background: "#71a241", borderColor: "#71a241" }}
        >
          {isEditMode ? "Update" : "Save Entry"}
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical" autoComplete="off">
        <div style={sectionStyle}>
          <FileTextOutlined style={{ fontSize: "16px" }} />
          <span>Invoice info (from PO)</span>
        </div>
        <Row gutter={16}>
          <Col xs={24} sm={8}>
            <Form.Item name="taxInvoiceNo" label="Tax Invoice No.">
              <Input placeholder="e.g. from OSG PI No" disabled />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item name="taxInvoiceDate" label="Tax Invoice Date">
              <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" disabled />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item name="invoiceAmount" label="Invoice Amount (₹)">
              <InputNumber
                style={currencyStyle}
                min={0}
                precision={2}
                disabled
                formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="paymentType" label="Payment type" rules={[{ required: true }]}>
          <Select placeholder="Select type" options={paymentTypeOptions} />
        </Form.Item>

        {(paymentType === PAYMENT_TYPE_PART_PAYMENT || paymentType === PAYMENT_TYPE_CREDIT_NOTE) &&
          dispatches.length > 0 && (
            <Form.Item name="dispatchId" label="Link to Dispatch">
              <Select
                placeholder="Select dispatch (optional)"
                options={dispatchOptions}
                allowClear
              />
            </Form.Item>
          )}

        {paymentType === PAYMENT_TYPE_ADVANCE && (
          <>
            <div style={{ ...sectionStyle, background: "linear-gradient(135deg, #11998e15 0%, #38ef7d15 100%)", border: "1px solid #11998e30", color: "#065f46" }}>
              <DollarOutlined />
              <span>Advance details</span>
            </div>
            {totalPoCost > 0 && (
              <div style={{ marginBottom: 16, display: "flex", gap: 12, flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 140, background: "#f0fdf4", border: "1px solid #22c55e30", borderRadius: 10, padding: "10px 16px" }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#065f46", textTransform: "uppercase", marginBottom: 4 }}>Balance after this advance</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#064e3b" }}>
                    ₹{Math.max(
                      0,
                      totalPoCost -
                        (isEditMode && editRecord?.paymentType === PAYMENT_TYPE_ADVANCE && editRecord.paymentAmount
                          ? totalAdvanceReceived - parseFloat(editRecord.paymentAmount)
                          : totalAdvanceReceived) -
                        (Number(advanceAmount) || 0)
                    ).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                  </div>
                </div>
              </div>
            )}
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item name="paymentMode" label="Payment mode" rules={[{ required: true }]}>
                  <Select placeholder="Select mode" options={paymentModeOptions} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="advanceAmount" label="Advance amount (₹)" rules={[{ required: true }]}>
                  <InputNumber
                    style={currencyStyle}
                    min={0}
                    precision={2}
                    formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item name="advanceDate" label="Advance date" rules={[{ required: true }]}>
                  <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="multiplePaymentRefNo" label="Payment reference number">
                  <Input placeholder="Reference no." />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}

        {paymentType === PAYMENT_TYPE_PART_PAYMENT && (
          <>
            {totalPoCost > 0 && (
              <div style={{ marginBottom: 16, display: "flex", gap: 12, flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 140, background: "#f0fdf4", border: "1px solid #22c55e30", borderRadius: 10, padding: "10px 16px" }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#065f46", textTransform: "uppercase", marginBottom: 4 }}>Balance before this payment</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#064e3b" }}>
                    ₹{previousBalance.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                  </div>
                </div>
              </div>
            )}
            <div style={{ ...sectionStyle, background: "linear-gradient(135deg, #11998e15 0%, #38ef7d15 100%)", border: "1px solid #11998e30", color: "#065f46" }}>
              <DollarOutlined />
              <span>Part payment details</span>
            </div>
            <Row gutter={16}>
              <Col xs={24} sm={12} md={8}>
                <Form.Item name="paymentDate" label="Payment date">
                  <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item name="paymentDueDate" label="Due date">
                  <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item name="paymentAmount" label="Payment amount (₹)" rules={[{ required: true }]}>
                  <InputNumber
                    style={currencyStyle}
                    min={0}
                    precision={2}
                    formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} sm={12} md={8}>
                <Form.Item name="tds" label="TDS (₹)">
                  <InputNumber
                    style={currencyStyle}
                    min={0}
                    precision={2}
                    formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item name="balanceAmount" label="Balance after (₹)">
                  <InputNumber
                    style={{ ...currencyStyle, background: "#f0fdf4", cursor: "default" }}
                    min={0}
                    precision={2}
                    readOnly
                    formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item name="noDuesClearanceStatus" label="No dues clearance">
                  <Select placeholder="Select" options={noDuesClearanceOptions} allowClear />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item name="paymentMode" label="Payment mode">
                  <Select placeholder="Select mode" options={paymentModeOptions} allowClear />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="multiplePaymentRefNo" label="Payment reference number">
                  <Input placeholder="Payment reference" />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}

        {paymentType === PAYMENT_TYPE_CREDIT_NOTE && (
          <>
            <div style={{ ...sectionStyle, background: "linear-gradient(135deg, #a18cd115 0%, #fbc2eb15 100%)", border: "1px solid #a18cd130", color: "#5b21b6" }}>
              <CreditCardOutlined />
              <span>Credit note details</span>
            </div>
            {(previousBalance > 0 ||
              (isEditMode &&
                editRecord?.paymentType === PAYMENT_TYPE_CREDIT_NOTE &&
                !!editRecord.paymentAmount)) && (
              <div style={{ marginBottom: 16, display: "flex", gap: 12, flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 140, background: "#f5f3ff", border: "1px solid #a18cd130", borderRadius: 10, padding: "10px 16px" }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#5b21b6", textTransform: "uppercase", marginBottom: 4 }}>Balance after this credit note</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#4c1d95" }}>
                    ₹{Math.max(
                      0,
                      (isEditMode && editRecord?.paymentType === PAYMENT_TYPE_CREDIT_NOTE && editRecord.paymentAmount
                        ? previousBalance + parseFloat(editRecord.paymentAmount)
                        : previousBalance) -
                        (Number(creditNoteAmount) || 0)
                    ).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                  </div>
                </div>
              </div>
            )}
            <Row gutter={16}>
              <Col xs={24} sm={8}>
                <Form.Item name="creditNoteRef" label="Credit note ref">
                  <Input placeholder="e.g. CN-2026-001" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="creditNoteDate" label="Credit note date">
                  <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="creditNoteAmount" label="Credit note amount (₹)">
                  <InputNumber
                    style={currencyStyle}
                    min={0}
                    precision={2}
                    formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="creditNoteRemark" label="Remarks">
              <Input.TextArea rows={2} placeholder="Optional remarks" />
            </Form.Item>
          </>
        )}
      </Form>
    </Modal>
  );
};

export default AddPaymentModal;
